/**
 * trusteePermissions.js — Source unique des droits per-horse côté frontend
 *
 * Pourquoi ce module existe (C3-S1, avril 2026)
 * ──────────────────────────────────────────────
 * Avant C3, quatre patterns divergents identifiaient le rôle de l'utilisateur
 * sur un cheval (HorseList, SessionListPage, AppNav, HorseEditModal). Chaque
 * pattern lisait soit `currentUser.role` (global, faux per-horse), soit
 * `shareRole` (string opaque), avec des règles métier inline. Résultat : un
 * utilisateur owner sur cheval A et trustee sur cheval B prenait des décisions
 * incohérentes selon l'écran. Ce module centralise la logique.
 *
 * Mapping legacy DB FR ↔ API anglaise
 * ────────────────────────────────────
 * Les enums Mongoose (`Share.guestRole`, `User.role`) datent de la v1 et sont
 * en français : 'personne_confiance', 'veterinaire'. Les renommer en DB
 * impliquerait une migration sur la collection `shares` (Atlas prod), ce qui
 * n'est pas dans le périmètre C3. On garde donc le FR côté DB et on expose
 * une API anglaise lisible côté code applicatif :
 *
 *   getContextualRole(...) → 'owner' | 'trustee' | 'vet' | null
 *
 * Les constantes TRUSTEE_DB / VET_DB sont volontairement INTERNES au module —
 * jamais exportées. Aucun appelant ne doit manipuler 'personne_confiance'
 * directement : si tu vois cette chaîne ailleurs dans le code, c'est un bug.
 *
 * Granularité C3-S1 : au bloc
 * ────────────────────────────
 * Pour cette livraison, le whitelist trustee est défini au niveau bloc
 * (location, feeding, temperature, humidity) et non au champ — cohérent avec
 * le whitelist backend `horseController.updateHorse`. Le split champ-par-champ
 * (location.address vs location.city, feeding.type2, etc.) est volontairement
 * reporté à C3-S2 si besoin terrain.
 *
 * Signature à 3 args
 * ───────────────────
 * getContextualRole(horse, currentUser, share) — `share` est explicite et
 * jamais reconstruit dans le module. L'appelant doit le fournir depuis
 * useAppState.shareRoles[horseId] : { role, acceptedAt } | null. Cette
 * contrainte rend impossible le piège "global currentUser.role" — sans share,
 * un utilisateur non-owner est non-autorisé, point.
 */

import i18n from '../i18n';

const TRUSTEE_DB = 'personne_confiance';
const VET_DB     = 'veterinaire';

/**
 * Labels FR des rôles (pour badges, messages, libellés UI).
 * Source unique de vérité côté frontend. Mirror de
 * `backend/constants/roles.js` ROLE_LABELS_FR — garder synchro.
 *
 * Note casse : ces labels sont utilisés en début de phrase (Vétérinaire,
 * Personne de confiance) — variante minuscule pour les milieux de phrase
 * disponible côté backend (`roleLabelFr`).
 */
export const ROLE_LABELS = Object.freeze({
  proprietaire: 'Propriétaire',
  veterinaire: 'Vétérinaire',
  personne_confiance: 'Personne de confiance',
  admin: 'Administrateur',
  super_admin: 'Super administrateur',
});

const TRUSTEE_ALLOWED_FIELDS = new Set([
  'housingType',
  'bedding',
  'location',
  'feeding',
]);

const TRUSTEE_ALLOWED_THRESHOLDS = new Set([
  'temperature',
  'humidity',
]);

// Champs réservés au propriétaire quel que soit le rôle de l'invité (10.5.295) :
// ils pilotent la création des partages. Mirror de backend/permissions/trusteeFields.js.
const OWNER_ONLY_FIELDS = new Set([
  'trustContacts',
  'veterinarian',
]);

function sameId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function getUserId(currentUser) {
  if (!currentUser) return null;
  return currentUser._id || currentUser.id || null;
}

/**
 * Rôle contextuel de l'utilisateur sur CE cheval.
 * @param {Object|null} horse        — horse document (must have ownerId)
 * @param {Object|null} currentUser  — { _id|id, ... }
 * @param {Object|null} share        — { role: DB_ENUM, acceptedAt: Date } | null
 * @returns {'owner'|'trustee'|'vet'|null}
 */
export function getContextualRole(horse, currentUser, share) {
  if (!horse || !currentUser) return null;
  const userId = getUserId(currentUser);
  if (!userId) return null;

  if (sameId(horse.ownerId, userId)) return 'owner';

  if (!share || !share.role) return null;
  if (share.role === TRUSTEE_DB) return 'trustee';
  if (share.role === VET_DB)     return 'vet';
  return null;
}

export function isTrustee(horse, currentUser, share) {
  return getContextualRole(horse, currentUser, share) === 'trustee';
}

export function isVet(horse, currentUser, share) {
  return getContextualRole(horse, currentUser, share) === 'vet';
}

export function isOwner(horse, currentUser, share) {
  return getContextualRole(horse, currentUser, share) === 'owner';
}

/**
 * Peut-on éditer ce champ sur ce cheval ?
 *
 * Convention : les champs du modèle Horse sont passés à plat ('horseName',
 * 'housingType', 'location'). Les champs de AlertThreshold sont préfixés par
 * 'thresholds.' ('thresholds.temperature', 'thresholds.pm25').
 *
 * Matrice (cf backend horseController.js:158-166 + alertController.js:87-148) :
 *   - owner  : tous les champs Horse + thresholds.temperature + thresholds.humidity
 *              (PM/exposureDuration réservés au véto)
 *   - vet    : tout (Horse + tous les thresholds) sauf trustContacts et
 *              veterinarian, réservés au propriétaire
 *   - trustee: housingType, bedding, location, feeding,
 *              thresholds.temperature, thresholds.humidity
 *   - super_admin (hors propriétaire) : tous les champs Horse, contacts et
 *              vétérinaire compris (10.5.295) et tous les thresholds, comme
 *              le véto (alertController, 10.5.296)
 *   - aucun  : false
 */
export function canEditField(field, horse, currentUser, share) {
  if (typeof field !== 'string' || !field) return false;
  const role = getContextualRole(horse, currentUser, share);
  // Le super_admin non propriétaire passe avant tout partage, comme côté
  // serveur (checkHorseAccess : branche admin avant les partages). Le plafond
  // des partages reste contrôlé par le serveur.
  if (role !== 'owner' && horse && isSuperAdminRole(currentUser?.role)) {
    return true;
  }
  if (role === null) return false;

  if (field.startsWith('thresholds.')) {
    const key = field.slice('thresholds.'.length);
    if (role === 'vet') return true;
    if (role === 'owner' || role === 'trustee') {
      return TRUSTEE_ALLOWED_THRESHOLDS.has(key);
    }
    return false;
  }

  if (role === 'owner') return true;
  if (OWNER_ONLY_FIELDS.has(field)) return false;
  if (role === 'vet') return true;
  if (role === 'trustee') return TRUSTEE_ALLOWED_FIELDS.has(field);
  return false;
}

function isAfterOrEqual(dateLike, refDateLike) {
  if (!dateLike || !refDateLike) return false;
  const t = new Date(dateLike).getTime();
  const r = new Date(refDateLike).getTime();
  if (Number.isNaN(t) || Number.isNaN(r)) return false;
  return t >= r;
}

/**
 * Peut-on éditer/supprimer une session existante ?
 *
 * Backend : utils/sessionAccess.js (PUT et DELETE /api/sessions/:id).
 *   - owner du cheval : toujours OK
 *   - super_admin : toujours OK, avant tout partage (10.5.296)
 *   - vet/trustee : doit être l'auteur de la session (session.userId)
 *   - trustee : en plus, session.start_time >= share.acceptedAt
 */
export function canEditSession(session, horse, currentUser, share) {
  const role = getContextualRole(horse, currentUser, share);
  if (session && horse && role !== 'owner' && isSuperAdminRole(currentUser?.role)) return true;
  if (role === null || !session) return false;
  if (role === 'owner') return true;

  const userId = getUserId(currentUser);
  if (!sameId(session.userId, userId)) return false;

  if (role === 'trustee') {
    if (!share || !share.acceptedAt) return false;
    return isAfterOrEqual(session.start_time, share.acceptedAt);
  }
  return true;
}

/**
 * Peut-on uploader une session pour cette date de début ?
 *
 * Pré-création : owner/vet libres, trustee borné à startTime >= acceptedAt.
 * Justification : un trustee qui pourrait uploader une session antidatée
 * antérieure à son partage ne pourrait ensuite ni la modifier ni la supprimer
 * (canEditSession refuse), créant une session orpheline. On bloque en amont.
 */
export function canUploadSession(startTime, horse, currentUser, share) {
  const role = getContextualRole(horse, currentUser, share);
  if (role === null) return false;
  if (role === 'owner' || role === 'vet') return true;
  if (!share || !share.acceptedAt || !startTime) return false;
  return isAfterOrEqual(startTime, share.acceptedAt);
}

// ─── String-only role helpers ─────────────────────────────────────────────────
// Les helpers ci-dessous opèrent sur la chaîne du rôle uniquement (jamais sur
// un triplet horse/currentUser/share). Usage : styling de badge, label UI,
// condition de visibilité au niveau du compte (signup-role global, pas
// per-horse). À ne PAS confondre avec isVet/isTrustee/isOwner per-horse
// au-dessus, qui décident d'un droit contextuel.

/**
 * Retourne le label FR d'un rôle (ou la chaîne d'entrée si inconnue).
 * Usage : badges, messages utilisateur, libellés de select.
 *
 * @param {string} role - Rôle interne ('veterinaire', 'personne_confiance', etc.)
 * @returns {string} - Label FR ('Vétérinaire', 'Personne de confiance')
 */
export function roleLabel(role) {
  if (!role) return '';
  // Namespace i18n transverse `roles.<codeInterne>` (vague 1.2). Util non-React :
  // on consomme l'instance i18n singleton (pas le hook). Fallback = code interne
  // si la clé manque (rôle inconnu). ROLE_LABELS reste le miroir FR canonique
  // (source des valeurs roles.* dans fr.json + référence backend roles.js).
  return i18n.t(`roles.${role}`, { defaultValue: role });
}

/**
 * Test string-only : ce rôle est-il 'veterinaire' ?
 * Usage : styling badge, filtre liste, condition d'affichage simple.
 * À ne PAS confondre avec isVet(horse, currentUser, share) qui est per-horse.
 *
 * @param {string} role - Chaîne rôle (currentUser.role, share.guestRole, etc.)
 * @returns {boolean}
 */
export function isVetRole(role) {
  return role === VET_DB;
}

/**
 * Test string-only : ce rôle est-il 'personne_confiance' ?
 * À ne PAS confondre avec isTrustee(horse, currentUser, share) qui est per-horse.
 *
 * @param {string} role
 * @returns {boolean}
 */
export function isTrusteeRole(role) {
  return role === TRUSTEE_DB;
}

/**
 * Test string-only : ce rôle global (User.role) est-il 'super_admin' ?
 *
 * @param {string} role
 * @returns {boolean}
 */
export function isSuperAdminRole(role) {
  return role === 'super_admin';
}

/**
 * Test : ce rôle global (User.role) est-il un rôle d'invité signup
 * (vétérinaire ou personne de confiance, par opposition à propriétaire / admin) ?
 * Usage : masquer sections owner-only dans la nav et le profil.
 *
 * @param {object|null} currentUser - Objet User avec champ `role`
 * @returns {boolean}
 */
export function isGuestSignupRole(currentUser) {
  if (!currentUser?.role) return false;
  return isGuestSignupRoleString(currentUser.role);
}

/**
 * Variant string de isGuestSignupRole, pour les contextes où on n'a que la
 * chaîne (formData.role dans SignupPage par exemple).
 *
 * @param {string} role
 * @returns {boolean}
 */
export function isGuestSignupRoleString(role) {
  return role === VET_DB || role === TRUSTEE_DB;
}
